import { AQLBaseQuery } from './queries';
import { AQLQuery, AQLQueryCondition, AQLQueryValue, AQLRangeCondition } from './types';
import { AQLFlatQueryBuilder } from './QueryBuilder';
import {
  ROOT_SEGMENT_ID,
  ALL_GARM_CATEGORIES,
  SegmentCreatorData,
  ALL_IAB_CATEGORIES,
} from '@insights_module/context/SegmentCreatorContext';
import { genRandomString } from '@utils/commonUtils';
import {
  CategoryType,
  GarmRiskLevelType,
  IabBrandSafetyCategoryType,
  IabBrandSafetyRiskLevelType,
} from '@insights_module/types/BrandSafety';
import {
  GARM_CATEGORIES_ARRAY,
  IAB_BRAND_SAFETY_CATEGORY_ARRAY,
} from '@insights_module/constants/garm';
import { IABFilterType } from '@insights_module/types/ContextualAndEntity';

export function mergeFilters(filters: AQLBaseQuery[]) {
  return filters.reduce((result, currentFilter) => {
    return { ...result, ...currentFilter };
  }, {});
}

export function groupQueryDataByGroupId(
  userInput: SegmentCreatorData[]
): Map<string | null, SegmentCreatorData[]> {
  const queryGroupMap = new Map();

  for (const input of userInput) {
    if (!queryGroupMap.has(input.groupId)) {
      queryGroupMap.set(input.groupId, []);
    }
    queryGroupMap.get(input.groupId).push(input);
  }

  return queryGroupMap;
}

export function buildQueryFromQueryData(
  queryData: Map<string | null, SegmentCreatorData[]>,
  builder: AQLFlatQueryBuilder,
  groupId: string | null = null
) {
  const currentGroup = queryData.get(groupId);
  if (!currentGroup) return;
  for (const input of currentGroup) {
    if (input.type === 'all' || input.type === 'any') {
      // Start a new composite query
      builder.start(input.type);

      // Recursively process queries within this composite group
      buildQueryFromQueryData(queryData, builder, input.id);

      builder.end(input.type);
    }
    // wrap filter in none composite query if negation is true
    if (input.negation) {
      builder.start('none');
    }

    if (input.type === 'brand_safety_garm' && input.category && input.condition && input.value) {
      // Add a garm filter query
      if (input.category === ALL_GARM_CATEGORIES) {
        // add all categories
        builder.addGarmQuery((q) =>
          GARM_CATEGORIES_ARRAY.forEach((category) => {
            q.addCategoryQuery(
              category as CategoryType,
              input.condition as Extract<AQLQueryCondition, 'eq' | 'any_of' | 'range'>,
              input.value as
                | GarmRiskLevelType
                | AQLRangeCondition<GarmRiskLevelType>
                | GarmRiskLevelType[]
            );
          })
        );
      } else {
        builder.addGarmQuery((q) =>
          q.addCategoryQuery(
            input.category as CategoryType,
            input.condition as Extract<AQLQueryCondition, 'eq' | 'any_of' | 'range'>,
            input.value as
              | GarmRiskLevelType
              | AQLRangeCondition<GarmRiskLevelType>
              | GarmRiskLevelType[]
          )
        );
      }
    }

    if (input.type === 'brand_safety_iab' && input.category && input.condition && input.value) {
      // Add a iab filter query
      if (input.category === ALL_IAB_CATEGORIES) {
        // add all categories
        builder.addIabBrandSafetyQuery((q) =>
          IAB_BRAND_SAFETY_CATEGORY_ARRAY.forEach((category) => {
            q.addIabBrandSafetyCategoryQuery(
              category as IabBrandSafetyCategoryType,
              input.condition as Extract<AQLQueryCondition, 'eq' | 'any_of' | 'range'>,
              input.value as
                | IabBrandSafetyRiskLevelType
                | AQLRangeCondition<IabBrandSafetyRiskLevelType>
                | IabBrandSafetyRiskLevelType[]
            );
          })
        );
      } else {
        builder.addIabBrandSafetyQuery((q) =>
          q.addIabBrandSafetyCategoryQuery(
            input.category as IabBrandSafetyCategoryType,
            input.condition as Extract<AQLQueryCondition, 'eq' | 'any_of' | 'range'>,
            input.value as
              | IabBrandSafetyRiskLevelType
              | AQLRangeCondition<IabBrandSafetyRiskLevelType>
              | IabBrandSafetyRiskLevelType[]
          )
        );
      }
    }

    if (input.type === 'contextual' && input.condition && input.value) {
      builder.addIABContextualQuery(
        input.condition as Extract<AQLQueryCondition, 'includes' | 'excludes'>,
        input.value as IABFilterType
      );
    }

    if (input.type === 'named_entities' && input.condition && input.value && input.operator) {
      builder.addNamedEntitiesQuery(
        input.condition as Extract<AQLQueryCondition, 'includes' | 'excludes'>,
        (q) => q.addNamedEntityQuery(input.operator as 'eq' | 'wildcard', input.value as string)
      );
    }

    if (
      input.type === 'linked_named_entities' &&
      input.condition &&
      input.value &&
      input.operator
    ) {
      builder.addLinkedNamedEntitiesQuery(
        input.condition as Extract<AQLQueryCondition, 'includes' | 'excludes'>,
        (q) =>
          q.addLinkedNamedEntitiesQuery(input.operator as 'eq' | 'wildcard', input.value as string)
      );
    }

    if (input.type === 'audio_count' && input.condition && input.value) {
      builder.addAudioCountQuery(
        input.condition as Extract<AQLQueryCondition, 'eq' | 'range'>,
        input.value as number | AQLRangeCondition<number>
      );
    }

    if (input.type === 'author' && input.value) {
      builder.addPodcastMetadataQuery((q) =>
        q.addPodcastMetadataFilterQuery('author', 'includes', input.value as string)
      );
    }

    if (input.type === 'apple_star_rating' && input.condition && input.value) {
      builder.addPodcastMetadataQuery((q) =>
        q.addPodcastMetadataFilterQuery(
          'apple_star_rating',
          'range',
          input.value as AQLRangeCondition<number>
        )
      );
    }

    if (input.type === 'apple_rating_count' && input.condition && input.value) {
      builder.addPodcastMetadataQuery((q) =>
        q.addPodcastMetadataFilterQuery(
          'apple_rating_count',
          'range',
          input.value as AQLRangeCondition<number>
        )
      );
    }

    if (input.type === 'apple_categories' && input.condition && input.value) {
      builder.addPodcastMetadataQuery((q) =>
        q.addPodcastMetadataFilterQuery(
          'apple_categories',
          input.condition as Extract<AQLQueryCondition, 'includes' | 'excludes'>,
          input.value as string
        )
      );
    }

    if (input.type === 'podcast_type' && input.value) {
      builder.addPodcastMetadataQuery((q) =>
        q.addPodcastMetadataFilterQuery('podcast_type', 'eq', input.value as string)
      );
    }

    if (input.type === 'name' && input.condition && input.value) {
      builder.addNameQuery(
        input.condition as Extract<AQLQueryCondition, 'includes' | 'excludes'>,
        input.value as string
      );
    }

    if (input.type === 'title' && input.condition && input.value) {
      builder.addTitleQuery(
        input.condition as Extract<AQLQueryCondition, 'includes' | 'excludes'>,
        input.value as string
      );
    }

    if (input.type === 'episodes_by_show_id' && input.condition && input.value) {
      // TODO: add any_of condition
      builder.addGroupQuery('includes', (q) =>
        q.addIDQuery(input.condition as Extract<AQLQueryCondition, 'eq'>, input.value as string)
      );
    }

    if (input.type === 'transcript' && input.condition && input.value) {
      builder.addTranscriptQuery(
        input.condition as Extract<AQLQueryCondition, 'includes' | 'excludes'>,
        input.value as string
      );
    }

    if (input.type === 'published_date' && input.condition && input.value) {
      builder.addPublishedDateQuery(
        input.condition as Extract<AQLQueryCondition, 'eq' | 'range'>,
        input.value as string | AQLRangeCondition<string>
      );
    }

    if (input.type === 'duration' && input.condition && input.value) {
      builder.addDurationQuery(
        input.condition as Extract<AQLQueryCondition, 'eq' | 'range'>,
        input.value as number | AQLRangeCondition<number>
      );
    }

    if (input.type === 'language' && input.condition && input.value) {
      builder.addLanguageQuery(
        input.condition as Extract<AQLQueryCondition, 'eq' | 'excludes' | 'any_of'>,
        input.value as string | string[]
      );
    }

    if (input.negation) {
      builder.end('none');
    }
  }
}

// TODO: add functionality for reading groups -> id (episodes_by_show_id)
export function createQueryDataFromQuery(
  query: AQLQueryValue[],
  groupId: string | null = null,
  parentCompositeType: string | null = null
): SegmentCreatorData[] {
  return query.flatMap((q) => {
    const hasKeys = Object.keys(q).length > 0;
    if (!hasKeys) return [];

    const [type, query] = Object.entries(q)[0];
    if (type === 'all' || type === 'any') {
      const composite: SegmentCreatorData = {
        groupId,
        type: type as 'all' | 'any',
        // if no groupId, then this is the root composite query
        id: groupId ? genRandomString(16) : ROOT_SEGMENT_ID,
        negation: false,
      };

      const children: SegmentCreatorData[] = createQueryDataFromQuery(query, composite.id, type);
      return [composite, ...children];
    } else if (type === 'brand_safety_garm') {
      const hasCategory = Object.keys(query as object).length > 0;
      if (!hasCategory) return [];

      // case with all categories with same condition and value
      if (Object.entries(query as object).length === GARM_CATEGORIES_ARRAY.length) {
        const [, filter] = Object.entries(query as object)[0];
        const [condition, value] = Object.entries(filter as object)[0];

        return {
          groupId,
          type,
          id: genRandomString(16),
          category: ALL_GARM_CATEGORIES,
          condition: condition as unknown as AQLQueryCondition,
          value:
            typeof value === 'string'
              ? (value?.toLowerCase() as unknown as GarmRiskLevelType)
              : value,
          negation: parentCompositeType === 'none',
        } as SegmentCreatorData;
      }

      const [category, filter] = Object.entries(query as object)[0];
      const hasFilter = Object.keys(filter as object).length > 0;
      if (!hasFilter) return [];

      const [condition, value] = Object.entries(filter as object)[0];
      return {
        groupId,
        type,
        id: genRandomString(16),
        category: category,
        condition: condition as unknown as AQLQueryCondition,
        value:
          typeof value === 'string'
            ? (value?.toLowerCase() as unknown as GarmRiskLevelType)
            : value,
        negation: parentCompositeType === 'none',
      } as SegmentCreatorData;
    } else if (type === 'brand_safety_iab') {
      const hasCategory = Object.keys(query as object).length > 0;
      if (!hasCategory) return [];

      // case with all categories with same condition and value
      if (Object.entries(query as object).length === IAB_BRAND_SAFETY_CATEGORY_ARRAY.length) {
        const [, filter] = Object.entries(query as object)[0];
        const [condition, value] = Object.entries(filter as object)[0];

        return {
          groupId,
          type,
          id: genRandomString(16),
          category: ALL_IAB_CATEGORIES,
          condition: condition as unknown as AQLQueryCondition,
          value:
            typeof value === 'string'
              ? (value?.toLowerCase() as unknown as IabBrandSafetyRiskLevelType)
              : value,
          negation: parentCompositeType === 'none',
        } as SegmentCreatorData;
      }

      const [category, filter] = Object.entries(query as object)[0];
      const hasFilter = Object.keys(filter as object).length > 0;
      if (!hasFilter) return [];

      const [condition, value] = Object.entries(filter as object)[0];
      return {
        groupId,
        type,
        id: genRandomString(16),
        category: category,
        condition: condition as unknown as AQLQueryCondition,
        value:
          typeof value === 'string'
            ? (value?.toLowerCase() as unknown as IabBrandSafetyRiskLevelType)
            : value,
        negation: parentCompositeType === 'none',
      } as SegmentCreatorData;
    } else if (type === 'contextual') {
      const hasFilter = Object.keys(query as object).length > 0;
      if (!hasFilter) return [];

      const [condition, value] = Object.entries(query as object)[0];

      return {
        groupId,
        type,
        id: genRandomString(16),
        // can be 'includes' or 'excludes'
        condition: condition as unknown as AQLQueryCondition,
        // for now we just use 'name' query and 'eq' condition for contextual and taxonomy is always 'iab'
        value: value?.name?.eq,
        negation: false,
      } as SegmentCreatorData;
    } else if (type === 'named_entities') {
      const hasFilter = Object.keys(query as object).length > 0;
      if (!hasFilter) return [];

      const [condition, term] = Object.entries(query as object)[0];
      const [operator, value] = Object.entries(term.value as object)[0];

      return {
        groupId,
        type,
        id: genRandomString(16),
        // can be 'includes' or 'excludes'
        condition: condition as unknown as AQLQueryCondition,
        operator: operator as 'eq' | 'wildcard',
        value: value as string,
        negation: false,
      } as SegmentCreatorData;
    } else if (type === 'linked_named_entities') {
      const hasFilter = Object.keys(query as object).length > 0;
      if (!hasFilter) return [];

      const [condition, term] = Object.entries(query as object)[0];
      const [operator, value] = Object.entries(term.id as object)[0];

      return {
        groupId,
        type,
        id: genRandomString(16),
        // can be 'includes' or 'excludes'
        condition: condition as unknown as AQLQueryCondition,
        operator: operator as 'eq',
        value: value as string,
        negation: false,
      } as SegmentCreatorData;
    } else if (type === 'audio_count' || type === 'duration') {
      const hasFilter = Object.keys(query as object).length > 0;
      if (!hasFilter) return [];
      const [condition, value] = Object.entries(query as object)[0];

      return {
        groupId,
        type,
        id: genRandomString(16),
        condition: condition as unknown as AQLQueryCondition,
        value: value as number | AQLRangeCondition<number>,
        negation: parentCompositeType === 'none',
      } as SegmentCreatorData;
    } else if (type === 'podcast_metadata') {
      const hasFilter = Object.keys(query as object).length > 0;
      if (!hasFilter) return [];
      const [subtype, value] = Object.entries(query as object)[0];
      const [condition, subValue] = Object.entries(value as object)[0];

      if (subtype === 'author') {
        return {
          groupId,
          type: 'author',
          id: genRandomString(16),
          value: subValue as string,
          negation: parentCompositeType === 'none',
        } as SegmentCreatorData;
      }
      if (subtype === 'apple_star_rating') {
        return {
          groupId,
          type: 'apple_star_rating',
          id: genRandomString(16),
          condition: 'range' as unknown as AQLQueryCondition,
          value: subValue as AQLRangeCondition<number>,
          negation: parentCompositeType === 'none',
        } as SegmentCreatorData;
      }
      if (subtype === 'apple_rating_count') {
        return {
          groupId,
          type: 'apple_rating_count',
          id: genRandomString(16),
          condition: 'range' as unknown as AQLQueryCondition,
          value: subValue as AQLRangeCondition<number>,
          negation: parentCompositeType === 'none',
        } as SegmentCreatorData;
      }
      if (subtype === 'podcast_type') {
        return {
          groupId,
          type: 'podcast_type',
          id: genRandomString(16),
          value: subValue as string,
          negation: parentCompositeType === 'none',
        } as SegmentCreatorData;
      }
      if (subtype === 'apple_categories') {
        return {
          groupId,
          type: 'apple_categories',
          id: genRandomString(16),
          condition: condition as unknown as AQLQueryCondition,
          value: subValue as string,
        } as SegmentCreatorData;
      }
    } else if (
      type === 'name' ||
      type === 'title' ||
      type === 'transcript' ||
      type === 'language'
    ) {
      const hasFilter = Object.keys(query as object).length > 0;
      if (!hasFilter) return [];
      const [condition, value] = Object.entries(query as object)[0];

      return {
        groupId,
        type,
        id: genRandomString(16),
        condition: condition as unknown as AQLQueryCondition,
        value: value as string | string[],
        negation: parentCompositeType === 'none',
      } as SegmentCreatorData;
    } else if (type === 'published_date') {
      const hasFilter = Object.keys(query as object).length > 0;
      if (!hasFilter) return [];
      const [condition, value] = Object.entries(query as object)[0];

      return {
        groupId,
        type,
        id: genRandomString(16),
        condition: condition as unknown as AQLQueryCondition,
        value: value as string | AQLRangeCondition<string>,
        negation: parentCompositeType === 'none',
      } as SegmentCreatorData;
    } else if (type === 'none') {
      const children: SegmentCreatorData[] = createQueryDataFromQuery(query, groupId, type);
      return children;
    }

    return [];
  });
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function createQueryFromLegacyViewData(filterObject: any): AQLQuery {
  const builder = new AQLFlatQueryBuilder();
  builder.start('all');

  //garm
  if (filterObject?.garm) {
    const values = Object.values(filterObject.garm);
    const isSame = values.every((value) => value === values[0]);
    if (isSame && values.length === GARM_CATEGORIES_ARRAY.length) {
      builder.addGarmQuery((q) => {
        Object.keys(filterObject.garm).forEach((category) => {
          q.addCategoryQuery(category as CategoryType, 'range', {
            gte: 'unrelated',
            lte: values[0] as GarmRiskLevelType,
          });
        });
      });
    } else {
      Object.entries(filterObject.garm).forEach(([category, value]) => {
        builder.addGarmQuery((q) =>
          q.addCategoryQuery(category as CategoryType, 'range', {
            gte: 'unrelated',
            lte: value as GarmRiskLevelType,
          })
        );
      });
    }
  }

  // iab
  if (filterObject?.iab) {
    Object.entries(filterObject.iab).forEach(([key, value]) => {
      if (value === 'includes' || value === 'excludes') {
        builder.addIABContextualQuery(value, key as IABFilterType);
      }
    });
  }

  // named_entities
  if (filterObject?.named_entities) {
    if (Array.isArray(filterObject.named_entities?.includes)) {
      filterObject.named_entities.includes.forEach((entity: string) => {
        builder.addNamedEntitiesQuery('includes', (q) => {
          q.addNamedEntityQuery('wildcard', entity);
        });
      });
    }
    if (Array.isArray(filterObject.named_entities?.excludes)) {
      filterObject.named_entities.excludes.forEach((entity: string) => {
        builder.addNamedEntitiesQuery('excludes', (q) => {
          q.addNamedEntityQuery('wildcard', entity);
        });
      });
    }
  }
  builder.end('all');

  return builder.build();
}
